import {
  formatDistanceStrict,
  format,
  parseISO,
  intervalToDuration,
  formatDuration as dateFnsFormatDuration,
  type Duration,
  differenceInMinutes,
  differenceInHours,
  differenceInDays,
  differenceInWeeks,
  isSameDay,
  isSameYear,
} from "date-fns";
import { format as formatTz } from "date-fns-tz/format";
import { toZonedTime } from "date-fns-tz";

export const diff = (
  start: string | number,
  end: string | number,
  {
    units = "long",
  }: Partial<{
    units: DurationUnits;
  }> = {},
): string | undefined => {
  if (start && end) {
    const distance = formatDistanceStrict(new Date(end), new Date(start), {
      roundingMethod: "floor",
    });

    return units === "short" ? convertToShortUnits(distance) : distance;
  }

  return undefined;
};

type DurationUnits = "short" | "long";
type DurationGranularity = "minute" | "second";

type DurationOptions = {
  units?: DurationUnits;
  granularity?: DurationGranularity;
};

// Helper for formatting durations, especially useful for durations that may be very long (eg. sync run times).
// >= 1 day: 1d 2h 3m
// >= 1 hour: 1h 2m
// >= 1 minute: 1m
// <1 minute: <1m
export const formatDuration = (
  { start, end }: { start: string | number; end: string | number },
  { units = "long", granularity = "minute" }: Partial<DurationOptions> = {},
): string | undefined => {
  if (start === "" || end === "") return undefined;

  const duration = intervalToDuration({ start, end });

  const formatted = isBelowGranularity(duration, granularity)
    ? granularity === "minute"
      ? "<1 minute"
      : "<1 second"
    : dateFnsFormatDuration(truncateLongDuration(duration, granularity), {
        zero: true,
      });

  return units === "short" ? convertToShortUnits(formatted) : formatted;
};

const isBelowGranularity = (
  duration: Duration,
  granularity: DurationGranularity,
) =>
  !duration.years &&
  !duration.months &&
  !duration.weeks &&
  !duration.days &&
  !duration.hours &&
  !duration.minutes &&
  (granularity === "minute" || !duration.seconds || duration.seconds < 0);

// Cleans up long durations to specific units depending on the biggest units.
// Also adds zeros to units we want to show even if they're 0.
const truncateLongDuration = (
  duration: Duration,
  granularity: DurationGranularity,
): Duration => {
  if (duration.days) {
    return {
      days: duration.days,
      hours: duration.hours ?? 0,
      minutes: duration.minutes ?? 0,
      seconds: granularity === "second" ? (duration.seconds ?? 0) : undefined,
    };
  }

  if (duration.hours) {
    return {
      hours: duration.hours,
      minutes: duration.minutes ?? 0,
      seconds: granularity === "second" ? (duration.seconds ?? 0) : undefined,
    };
  }

  if (duration.minutes) {
    return {
      minutes: duration.minutes,
      seconds: granularity === "second" ? (duration.seconds ?? 0) : undefined,
    };
  }

  if (duration.seconds && granularity === "second") {
    return {
      seconds: duration.seconds,
    };
  }

  // Undefined if duration doesn't fit patterns above,
  // just return the original duration.
  return duration;
};

const convertToShortUnits = (formatted: string): string =>
  formatted
    .replace(/\s+day(s?)\b/g, "d")
    .replace(/\s+hour(s?)\b/g, "h")
    .replace(/\s+minute(s?)\b/g, "m")
    .replace(/\s+second(s?)\b/g, "s");

export const getDuration = (
  startedAt: string | null | undefined,
  finishedAt: string | null | undefined,
  options?: Partial<DurationOptions>,
): string => {
  if (!startedAt) return "--";

  return (
    formatDuration(
      {
        start: new Date(startedAt).getTime(),
        end: new Date(finishedAt ?? new Date().toUTCString()).getTime(),
      },
      { units: "short", ...options },
    ) ?? "--"
  );
};

export const formatDatetime = (
  timestamp: string,
  pattern = "MM/dd/yy 'at' p",
  opts: { unknownTimestampFormat: boolean } = { unknownTimestampFormat: false },
) => {
  try {
    return opts.unknownTimestampFormat
      ? format(timestamp, pattern)
      : format(parseISO(timestamp), pattern);
  } catch (e) {
    console.warn(e, timestamp, pattern);
    return null;
  }
};

export const formatDate = (
  timestamp: Date | string,
  opts?: {
    /**
     * When true, the date is formatted as-is in UTC rather than in the
     * user's local timezone. Use this for warehouse DATE values that arrive
     * as midnight-UTC timestamps (e.g. "2023-12-14T00:00:00.000Z") —
     * without it, local-timezone conversion can shift the displayed date
     * back by one day.
     */
    utc?: boolean;
  },
) => {
  try {
    const parsed =
      typeof timestamp == "string" ? parseISO(timestamp) : timestamp;
    return format(opts?.utc ? toZonedTime(parsed, "UTC") : parsed, "MM/dd/yy");
  } catch (e) {
    console.warn(e);
    return null;
  }
};

// This is the new preferred date format: https://www.notion.so/hightouch/UX-Writing-Guide-1e9ab9c473d5808d8c24c7dd295b205d#1e9ab9c473d5802ba78ac2922a7bc5f3
//
// This should become the default long-term, but changing it would break existing layout.
export const formatDateLong = (timestamp: Date | string) => {
  try {
    return format(
      typeof timestamp == "string" ? parseISO(timestamp) : timestamp,
      "MMMM d, yyyy",
    );
  } catch (e) {
    console.warn(e);
    return null;
  }
};

// The preferred date-and-time format ("May 4, 2025 at 10:25pm PST"), in the viewer's time zone.
export const formatDatetimeLong = (timestamp: Date | string) => {
  try {
    const date =
      typeof timestamp === "string" ? parseISO(timestamp) : timestamp;
    return formatTz(date, "MMMM d, yyyy 'at' h:mmaaa zzz");
  } catch (e) {
    console.warn(e, timestamp);
    return null;
  }
};

// Month and day only ("August 15"), for dates already bounded to the recent
// past — where the year is noise rather than information.
export const formatMonthDay = (timestamp: Date | string) => {
  try {
    return format(
      typeof timestamp == "string" ? parseISO(timestamp) : timestamp,
      "MMMM d",
    );
  } catch (e) {
    console.warn(e);
    return null;
  }
};

// Weekday-qualified short date ("Wed, Mar 18") for dense pickers where the
// weekday carries more meaning than the year — e.g. choosing a campaign run.
export const formatWeekdayShort = (timestamp: Date | string) => {
  try {
    return format(
      typeof timestamp == "string" ? parseISO(timestamp) : timestamp,
      "EEE, MMM d",
    );
  } catch (e) {
    console.warn(e);
    return null;
  }
};

export const formatTime = (timestamp: Date | string) => {
  try {
    return format(
      typeof timestamp == "string" ? parseISO(timestamp) : timestamp,
      "p",
    );
  } catch (e) {
    console.warn(e);
    return null;
  }
};

export const formatDateOrDatetime = (
  timestamp: string,
  includeTime: boolean,
) => {
  return includeTime ? formatDatetime(timestamp) : formatDate(timestamp);
};

// expects epoch in milliseconds
export const formatDateOrDatetimeFromEpoch = (epoch: number) => {
  try {
    return formatDateOrDatetime(new Date(epoch).toISOString(), true);
  } catch (e) {
    console.warn(e);
    return null;
  }
};

export const formatUTC = (timestamp: number) => {
  try {
    return format(toZonedTime(timestamp, "UTC"), "MM/dd/yy 'at' p");
  } catch (e) {
    console.warn(e);
    return null;
  }
};

/**
 * @returns A string like "3 hours ago", "Just now", "1 week from now"
 */
export const formatFriendlyDistanceToNow = (
  timestampString: string,
  nowString?: string,
) => {
  try {
    const timestamp = parseISO(timestampString);
    const now = nowString ? parseISO(nowString) : new Date();

    return differenceInMinutes(now, timestamp) < 1 &&
      now.getTime() >= timestamp.getTime()
      ? "Just now"
      : formatDistanceStrict(timestamp, now, {
          addSuffix: true,
        });
  } catch (e) {
    console.warn(e);
    return "";
  }
};

// Compact distance-to-now for dense surfaces ("4h ago", "6d ago", "2w ago",
// falling back to "Feb 2" / "Feb 2, 2026" past ~4 weeks). Without the suffix,
// "4h", "6d" and "now", for a column of times.
export const formatRelativeShort = (
  timestamp: string,
  { addSuffix = true }: { addSuffix?: boolean } = {},
): string => {
  const ago = addSuffix ? " ago" : "";
  try {
    const date = parseISO(timestamp);
    const now = new Date();
    const hours = differenceInHours(now, date);
    if (hours < 1) return addSuffix ? "Just now" : "now";
    if (hours < 24) return `${hours}h${ago}`;
    const days = differenceInDays(now, date);
    if (days < 7) return `${days}d${ago}`;
    const weeks = differenceInWeeks(now, date);
    if (weeks < 5) return `${weeks}w${ago}`;
    return format(date, isSameYear(date, now) ? "MMM d" : "MMM d, yyyy");
  } catch {
    return "";
  }
};

/**
 * @returns A string like "Today" or "June 6" or "June 6, 2021" if not in the current year
 */
export const formatFriendlyDay = (
  timestampString: string,
  nowString?: string,
) => {
  try {
    const timestamp = parseISO(timestampString);
    const now = nowString ? parseISO(nowString) : new Date();

    if (isSameDay(now, timestamp)) {
      return "Today";
    }

    if (isSameYear(now, timestamp)) {
      return format(timestamp, "MMMM d");
    }

    return format(timestamp, "MMMM d, yyyy");
  } catch (e) {
    console.warn(e);
    return "";
  }
};

/** Helper function to convert interval to milliseconds */
export const getIntervalInMs = (interval: string): number => {
  switch (interval) {
    case "minute":
      // 1000ms * 60s
      return 1000 * 60;
    case "hour":
      // 1000ms * 60s * 60m
      return 1000 * 60 * 60;
    case "day":
      // 1000ms * 60s * 60m * 24h
      return 1000 * 60 * 60 * 24;
    case "week":
      // 1000ms * 60s * 60m * 24h * 7d
      return 1000 * 60 * 60 * 24 * 7;
    case "month":
      // 1000ms * 60s * 60m * 24h * 30d
      return 1000 * 60 * 60 * 24 * 30;
    case "year":
      // 1000ms * 60s * 60m * 24h * 365d
      return 1000 * 60 * 60 * 24 * 365;
    default:
      return 0;
  }
};

export const formatShorthandDateTime = (timestampString: string) =>
  `${formatFriendlyDay(timestampString)} · ${format(timestampString, "h:mm aa")}`;

export const formatDatetimeWithTimezone = (timestamp: string | Date) => {
  try {
    const date =
      typeof timestamp === "string" ? parseISO(timestamp) : timestamp;
    return formatTz(date, "MM/dd/yy 'at' h:mm aa zzz");
  } catch (e) {
    console.warn(e, timestamp);
    return null;
  }
};

/**
 * Short timezone abbreviation (e.g. "EDT", "PST") for an IANA timezone.
 * Computed at the given instant so DST is correct — the same zone yields
 * "EST" in January and "EDT" in July.
 */
export const formatTimezoneAbbreviation = (
  timezone: string,
  instant: Date,
): string | null => {
  try {
    const parts = new Intl.DateTimeFormat(undefined, {
      timeZone: timezone,
      timeZoneName: "short",
    }).formatToParts(instant);
    return parts.find((p) => p.type === "timeZoneName")?.value ?? null;
  } catch (e) {
    console.warn(e, timezone);
    return null;
  }
};

/**
 * Floors a timestamp to the nearest minute boundary.
 * Useful for grouping events that occur at roughly the same time.
 */
export const roundDateForGrouping = (date: Date | string): Date => {
  const d = typeof date === "string" ? new Date(date) : date;
  return new Date(Math.floor(d.getTime() / 60000) * 60000);
};

/**
 * Returns the next whole hour in UTC formatted as "HH:mm".
 */
export const getNextWholeHourUTC = (): string => {
  const date = new Date();
  date.setUTCHours(date.getUTCHours() + 1, 0, 0, 0);
  return `${String(date.getUTCHours()).padStart(2, "0")}:${String(date.getUTCMinutes()).padStart(2, "0")}`;
};

export type RecencyBucket = "Today" | "This week" | "Older";

/**
 * Buckets a timestamp for grouped activity lists (e.g. the audience agent's
 * chat list): calendar-day "Today", then a rolling seven days for "This week"
 * — a Monday-anchored week would file Sunday's chats under "Older" on a
 * Monday morning, which reads wrong for recent activity.
 */
export const getRecencyBucket = (
  timestamp: Date | string,
  now: Date = new Date(),
): RecencyBucket => {
  const date = typeof timestamp === "string" ? parseISO(timestamp) : timestamp;
  if (isSameDay(date, now)) return "Today";
  if (differenceInDays(now, date) < 7) return "This week";
  return "Older";
};

export const RECENCY_BUCKET_ORDER: readonly RecencyBucket[] = [
  "Today",
  "This week",
  "Older",
];
